export enum CobrandIdentifier {
  WELLFRAME = 'wellframe',
  HARVARD_PILGRIM = 'hphc',
  BCBS_SOUTH_CAROLINA = 'bcbssc',
  BCBS_KANSAS_CITY = 'bcbskc',
  BCBS_TENNESSEE = 'bcbst',
  HSCSN = 'hscsn',
  CARE_FIRST = 'carefirst',
  CARE_ELEVATE = 'CareElevate',
}

// Not exporting the enum to prevent direct access of AppSku without the actual cobrand value
export enum AppSku {
  WELLFRAME = 'Wellframe',
  HARVARD_PILGRIM = 'MyConnect',
  BCBS_SOUTH_CAROLINA = 'BCBS Planner',
  BCBS_KANSAS_CITY = 'Care Mgmt',
  BCBS_TENNESSEE = 'CareTN',
  HSCSN = 'EasyConnect',
  CARE_FIRST = 'CareFirst',
  CARE_ELEVATE = 'Medica',
}

export interface Cobrand {
  id: CobrandIdentifier;
  sku: AppSku;
  displayName: string;
  androidPackageName: string;
  iosAppStoreID: string;
  host?: string;
}

export const CobrandMap: Record<CobrandIdentifier, Cobrand> = {
  [CobrandIdentifier.WELLFRAME]: {
    id: CobrandIdentifier.WELLFRAME,
    sku: AppSku.WELLFRAME,
    displayName: 'Wellframe',
    androidPackageName: 'com.wellfra.me',
    iosAppStoreID: '578550282',
    // No host - default cobrand
  },
  [CobrandIdentifier.HARVARD_PILGRIM]: {
    id: CobrandIdentifier.HARVARD_PILGRIM,
    sku: AppSku.HARVARD_PILGRIM,
    displayName: 'MyConnect',
    androidPackageName: 'com.hphc.wellframe',
    iosAppStoreID: '1526468112',
  },
  [CobrandIdentifier.BCBS_SOUTH_CAROLINA]: {
    id: CobrandIdentifier.BCBS_SOUTH_CAROLINA,
    sku: AppSku.BCBS_SOUTH_CAROLINA,
    displayName: 'BCBS Planner',
    androidPackageName: 'com.myhealthplanner.mobile',
    iosAppStoreID: '1485404237',
  },
  [CobrandIdentifier.BCBS_KANSAS_CITY]: {
    id: CobrandIdentifier.BCBS_KANSAS_CITY,
    sku: AppSku.BCBS_KANSAS_CITY,
    displayName: 'Care Mgmt',
    androidPackageName: 'com.bluekc.wellframe',
    iosAppStoreID: '1513957007',
    host: 'bluekc',
  },
  [CobrandIdentifier.BCBS_TENNESSEE]: {
    id: CobrandIdentifier.BCBS_TENNESSEE,
    sku: AppSku.BCBS_TENNESSEE,
    displayName: 'CareTN',
    androidPackageName: 'com.bcbst.wellframe.app',
    iosAppStoreID: '1494382785',
    host: 'caretn',
  },
  [CobrandIdentifier.HSCSN]: {
    id: CobrandIdentifier.HSCSN,
    sku: AppSku.HSCSN,
    displayName: 'EasyConnect Health',
    androidPackageName: 'com.hscsn.wellframe',
    iosAppStoreID: '6702030364',
  },
  [CobrandIdentifier.CARE_FIRST]: {
    id: CobrandIdentifier.CARE_FIRST,
    sku: AppSku.CARE_FIRST,
    displayName: 'CareFirst Care Management',
    androidPackageName: 'com.carefirst.wellframe',
    iosAppStoreID: '6754857410',
    host: 'carefirst',
  },
  [CobrandIdentifier.CARE_ELEVATE]: {
    id: CobrandIdentifier.CARE_ELEVATE,
    sku: AppSku.CARE_ELEVATE,
    displayName: 'Care Elevate',
    androidPackageName: 'com.medica.wellframe',
    iosAppStoreID: '6760660368',
    host: 'careelevate',
  },
};

const COBRAND_IDENTIFIER_LOOKUP: Record<string, CobrandIdentifier> = Object.values(
  CobrandIdentifier,
).reduce(
  (lookupMap, cobrandId) => ({
    ...lookupMap,
    [cobrandId.toLowerCase()]: cobrandId,
  }),
  {} as Record<string, CobrandIdentifier>,
);

// URL path segments can vary in casing (e.g. /careelevate vs /CareElevate), while
// our app logic expects a single canonical CobrandIdentifier value. This lookup lets
// us accept case-insensitive input and always map to the canonical enum member.
export function normalizeCobrandPathParam(
  cobrandPathParam: string | undefined,
): CobrandIdentifier | undefined {
  if (!cobrandPathParam) return undefined;
  return COBRAND_IDENTIFIER_LOOKUP[cobrandPathParam.toLowerCase()];
}

export function isCobrand(cobrandPathParam: string | undefined) {
  // Path is undefined -> We are on the Wellframe cobrand
  if (!cobrandPathParam) return true;
  // Path is defined and matches up with a cobrand -> We are on a non-Wellframe cobrand
  return Boolean(normalizeCobrandPathParam(cobrandPathParam));
}

export function parseServerSku(sku: string): AppSku {
  const appSkuValues = Object.values(AppSku) as string[];
  if (appSkuValues.includes(sku)) {
    return sku as AppSku;
  }
  // eslint-disable-next-line no-console
  console.warn(`Unrecognized server SKU: ${sku}, defaulting to Wellframe`);
  return AppSku.WELLFRAME;
}
